import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Icon } from "@iconify/react";
import Common from "../../../helper/common";
import {
  ResultStatus,
} from "../../../constants/appConstant";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { Input } from "../../../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../components/ui/tabs";
import { Formik } from "formik";
import { FormikInput } from "../../../components/formik-components";
import { AppCheckBox } from "../../../components/appComponents";
import * as yup from "yup";

const ReorderRoutes = ({
  open,
  setOpen,
  onClose,
  routes,
  menuTabs,
  onReorder,
}) => {
  const [treeData, setTreeData] = useState([]);
  const [originalRoutesData, setOriginalRoutesData] = useState(new Map()); // Store original route data for comparison
  const [originalMenuTabsData, setOriginalMenuTabsData] = useState(new Map()); // Store original menu tab data for comparison
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [editingSortOrder, setEditingSortOrder] = useState(null);
  const [sortOrderValue, setSortOrderValue] = useState("");
  const [activeTab, setActiveTab] = useState("reorder");
  const [menuTabsData, setMenuTabsData] = useState([]);
  const [menuTabEditId, setMenuTabEditId] = useState(null);
  const [menuTabSelectedItem, setMenuTabSelectedItem] = useState(null);
  const [menuTabFormOpen, setMenuTabFormOpen] = useState(false);
  const [isMenuTabSubmitting, setIsMenuTabSubmitting] = useState(false);

  // Build tree structure from flat list - same as addRoutes.js
  const buildTree = (items) => {
    const map = {};
    const roots = {};
    const OTHERS_KEY = 'others';
    const OTHERS_TITLE = 'Others (No Menutab)';

    // 1. Create map of all nodes
    items.forEach((item) => {
      map[item.id] = {
        ...item,
        key: item.id,
        value: item.id,
        title: item.name,
        children: []
      };
      // Create menutab root if missing
      if (item.menutab) {
        if (!roots[item.menutab]) {
          roots[item.menutab] = {
            key: `menutab-${item.menutabid}`,
            value: `menutab-${item.menutabid}`,
            title: item.menutab,
            isMenutab: true,
            menutabid: item.menutabid,
            children: []
          };
        }
      } else {
        // For items with no menutab, create 'Others' root if not exists
        if (!roots[OTHERS_KEY]) {
          roots[OTHERS_KEY] = {
            key: OTHERS_KEY,
            value: OTHERS_KEY,
            title: OTHERS_TITLE,
            isMenutab: true,
            menutabid: null,
            children: []
          };
        }
      }
    });

    // 2. Link parent-child relationships
    items.forEach((item) => {
      if (item.parentId && map[item.parentId]) {
        map[item.parentId].children.push(map[item.id]);
      } else if (item.menutab) {
        roots[item.menutab].children.push(map[item.id]);
      } else {
        roots[OTHERS_KEY].children.push(map[item.id]);
      }
    });

    // 3. Sort by sortOrder
    const sortByOrder = (list) => {
      return list
        .sort((a, b) => (a.sortOrder ?? a.id ?? 0) - (b.sortOrder ?? b.id ?? 0))
        .map((node) => ({
          ...node,
          children: sortByOrder(node.children),
        }));
    };

    // 4. Sort menu tabs and return as array
    const sortedRoots = Object.values(roots).sort((a, b) => {
      if (a.isMenutab && b.isMenutab) {
        // Sort menu tabs by their order from menuTabs array
        const aTab = menuTabs.find(tab => tab.name === a.title);
        const bTab = menuTabs.find(tab => tab.name === b.title);
        const orderA = aTab?.sortOrder ?? aTab?.id ?? 0;
        const orderB = bTab?.sortOrder ?? bTab?.id ?? 0;
        return orderA - orderB;
      }
      return 0;
    });

    return sortedRoots.map(root => ({
      ...root,
      children: sortByOrder(root.children)
    }));
  };

  // Toggle expand/collapse
  const toggleExpand = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  // Fetch menu tabs
  const fetchMenuTabs = async () => {
    try {
      const response = await Common.ApiService.getInstance().request(
        "GetMenuTabs",
        "GET"
      );
      if (response?.data?.length) {
        let _data = response.data.map((item) => ({
          id: item.id,
          name: item.name ?? "-",
          isactive: item.isactive === true ? "Active" : "Inactive",
          createdOn: item.createdOn ? new Date(item.createdOn).toLocaleDateString() : "-",
          sortOrder: item.sortOrder ?? item.id,
          isactiveValue: item.isactive === true,
        }));
        setMenuTabsData(_data);
      } else {
        setMenuTabsData([]);
      }
    } catch (error) {
      console.error("Error fetching menu tabs:", error);
      setMenuTabsData([]);
    }
  };

  useEffect(() => {
    if (routes && routes.length > 0 && menuTabs) {
      const tree = buildTree(routes);
      setTreeData(tree);
      // Initialize all menu tabs as expanded
      const menuTabKeys = tree.filter(item => item.isMenutab).map(item => item.key);
      setExpandedItems(new Set(menuTabKeys));
      
      // Store original route data for comparison
      const routesMap = new Map();
      routes.forEach(route => {
        routesMap.set(route.id, {
          id: route.id,
          sortOrder: route.sortOrder ?? null,
          parentId: route.parentId ?? null,
          menutabid: route.menutabid ?? null,
        });
      });
      setOriginalRoutesData(routesMap);
      
      // Store original menu tabs data for comparison
      const menuTabsMap = new Map();
      menuTabs.forEach(tab => {
        menuTabsMap.set(tab.id, {
          id: tab.id,
          sortOrder: tab.sortOrder ?? tab.id,
        });
      });
      setOriginalMenuTabsData(menuTabsMap);
    }
  }, [routes, menuTabs]);

  useEffect(() => {
    if (open && activeTab === "menutabs") {
      fetchMenuTabs();
    }
  }, [open, activeTab]);

  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, item, parentId = null) => {
    e.preventDefault();
    
    // Allow dropping menu tabs only on other menu tabs
    if (draggedItem?.isMenutab && !item.isMenutab) {
      e.dataTransfer.dropEffect = "none";
      return;
    }
    
    // Prevent dropping regular items on menu tabs
    if (!draggedItem?.isMenutab && item.isMenutab) {
      e.dataTransfer.dropEffect = "none";
      return;
    }
    
    e.dataTransfer.dropEffect = "move";
    setDragOverItem({ item, parentId });
  };

  const handleDrop = (e, targetItem, targetParentId = null) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.id === targetItem.id) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Handle menu tab reordering (menu tabs can only be reordered among themselves)
    if (draggedItem.isMenutab && targetItem.isMenutab) {
      const newTree = [...treeData];
      const draggedIndex = newTree.findIndex(item => (item.id || item.key) === (draggedItem.id || draggedItem.key));
      const targetIndex = newTree.findIndex(item => (item.id || item.key) === (targetItem.id || targetItem.key));
      
      if (draggedIndex !== -1 && targetIndex !== -1) {
        const [draggedNode] = newTree.splice(draggedIndex, 1);
        newTree.splice(targetIndex, 0, draggedNode);
        setTreeData(newTree);
      }
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Regular route/menu item reordering (cannot drop on menu tabs)
    if (targetItem.isMenutab || draggedItem.isMenutab) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Deep clone tree
    const cloneTree = (tree) => {
      return tree.map(item => ({
        ...item,
        children: item.children ? cloneTree(item.children) : []
      }));
    };

    const newTree = cloneTree(treeData);
    
    // Remove dragged item from its current position
    const removeFromTree = (list, id) => {
      for (let i = 0; i < list.length; i++) {
        if (list[i].id === id) {
          return list.splice(i, 1)[0];
        }
        if (list[i].children) {
          const found = removeFromTree(list[i].children, id);
          if (found) return found;
        }
      }
      return null;
    };

    const draggedNode = removeFromTree(newTree, draggedItem.id);
    if (!draggedNode) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Find target and insert dragged node
    const insertAtTarget = (list, targetId, node, targetParentId, menuTabId = null) => {
      for (let i = 0; i < list.length; i++) {
        const currentItem = list[i];
        const currentMenuTabId = currentItem.isMenutab ? currentItem.menutabid : menuTabId;
        
        if (currentItem.id === targetId && !currentItem.isMenutab) {
          // Insert before target
          const targetIndex = i;
          const newNode = {
            ...node,
            parentId: targetParentId || currentItem.parentId,
            menutabid: currentItem.menutabid || node.menutabid || currentMenuTabId,
          };
          list.splice(targetIndex, 0, newNode);
          return true;
        }
        if (currentItem.children && insertAtTarget(currentItem.children, targetId, node, targetParentId || (currentItem.isMenutab ? null : currentItem.id), currentMenuTabId)) {
          return true;
        }
      }
      return false;
    };

    insertAtTarget(newTree, targetItem.id, draggedNode, targetParentId);
    setTreeData(newTree);
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  // Get current sort order for an item
  const getCurrentSortOrder = (item, parentList = null) => {
    if (item.isMenutab) {
      return treeData.findIndex(tab => (tab.id || tab.key) === (item.id || item.key)) + 1;
    }
    // For regular items, find their position in their parent's children or in the menu tab's children
    const findInTree = (list, targetId) => {
      for (let i = 0; i < list.length; i++) {
        if ((list[i].id || list[i].key) === targetId) {
          return i + 1;
        }
        if (list[i].children) {
          const found = findInTree(list[i].children, targetId);
          if (found !== null) return found;
        }
      }
      return null;
    };
    return findInTree(treeData, item.id || item.key) || item.sortOrder || "-";
  };

  // Handle sort order input change
  const handleSortOrderChange = (item, newValue) => {
    const numValue = parseInt(newValue);
    if (isNaN(numValue) || numValue < 1) {
      setSortOrderValue(newValue);
      return;
    }

    // Deep clone tree
    const cloneTree = (tree) => {
      return tree.map(item => ({
        ...item,
        children: item.children ? cloneTree(item.children) : []
      }));
    };

    const newTree = cloneTree(treeData);
    const itemId = item.id || item.key;

    // Find the item and its parent list in the tree
    const findItemAndParent = (list, targetId, parentList = null, parentIndex = -1) => {
      for (let i = 0; i < list.length; i++) {
        if ((list[i].id || list[i].key) === targetId) {
          return { item: list[i], parentList: list, index: i };
        }
        if (list[i].children) {
          const found = findItemAndParent(list[i].children, targetId, list, i);
          if (found) return found;
        }
      }
      return null;
    };

    const found = findItemAndParent(newTree, itemId);
    if (!found) {
      cancelEditingSortOrder();
      return;
    }

    const { item: foundItem, parentList, index } = found;

    // Remove item from current position
    parentList.splice(index, 1);

    // Calculate new index (0-based)
    const newIndex = Math.min(Math.max(0, numValue - 1), parentList.length);

    // Insert at new position
    parentList.splice(newIndex, 0, foundItem);

    setTreeData(newTree);
    setEditingSortOrder(null);
    setSortOrderValue("");
  };

  // Start editing sort order
  const startEditingSortOrder = (item) => {
    const currentOrder = getCurrentSortOrder(item);
    setEditingSortOrder(item.id || item.key);
    setSortOrderValue(currentOrder.toString());
  };

  // Cancel editing
  const cancelEditingSortOrder = () => {
    setEditingSortOrder(null);
    setSortOrderValue("");
  };

  // Client-side validation before submit
  const validateSortOrders = (menus) => {
    const sortOrderMap = new Map();
    const conflicts = [];
    
    menus.forEach(menu => {
      const key = `${menu.menutabid || 'null'}_${menu.parentId || 'null'}_${menu.sortOrder}`;
      
      if (sortOrderMap.has(key)) {
        conflicts.push({
          menu1: sortOrderMap.get(key),
          menu2: menu,
          sortOrder: menu.sortOrder
        });
      } else {
        sortOrderMap.set(key, menu);
      }
    });
    
    return conflicts;
  };

  // Flatten tree to get all items with updated sortOrder (skip menu tab nodes)
  const flattenTree = (tree, parentId = null, startOrder = 1, menuTabId = null) => {
    let order = startOrder;
    const result = [];

    tree.forEach((item) => {
      // Skip menu tab nodes (they are just containers)
      if (item.isMenutab) {
        // Process children of menu tab
        if (item.children && item.children.length > 0) {
          const children = flattenTree(item.children, null, order, item.menutabid);
          result.push(...children);
          order += children.length;
        }
      } else {
        // Regular route/menu item
        const flatItem = {
          id: item.id,
          name: item.name,
          endpoint: item.endpoint,
          parentId: parentId,
          menutabid: menuTabId || item.menutabid,
          sortOrder: order++,
          ispremium: item.ispremium,
          isdisplayon: item.isdisplayon,
          isclubspecific: item.isclubspecific,
          isnewfeature: item.isnewfeature,
          icon: item.icon,
        };

        result.push(flatItem);

        // Process children
        if (item.children && item.children.length > 0) {
          const children = flattenTree(item.children, item.id, order, menuTabId || item.menutabid);
          result.push(...children);
          order += children.length;
        }
      }
    });

    return result;
  };

  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      // ============================================
      // 1. PROCESS MENU TABS - Only save changed ones
      // ============================================
      const menuTabsToSave = [];
      const menuTabsInTree = treeData.filter(item => item.isMenutab && item.menutabid !== null);
      
      menuTabsInTree.forEach((menuTab, index) => {
        const newSortOrder = index + 1;
        const originalData = originalMenuTabsData.get(menuTab.menutabid);
        const originalSortOrder = originalData?.sortOrder ?? menuTab.menutabid;
        
        // Only save if sortOrder changed
        if (newSortOrder !== originalSortOrder) {
          const originalTab = menuTabs.find(tab => tab.id === menuTab.menutabid || tab.name === menuTab.title);
          menuTabsToSave.push({
            id: menuTab.menutabid,
            name: originalTab?.name || menuTab.title,
            isactive: originalTab?.isactive ?? true,
            sortOrder: newSortOrder,
            originalSortOrder: originalSortOrder,
          });
        }
      });

      // ============================================
      // 2. PROCESS ROUTES - Only save items with changed sortOrder
      // ============================================
      const flatItems = flattenTree(treeData);
      const menusWithChangedSortOrder = [];
      
      flatItems.forEach((item) => {
        const originalData = originalRoutesData.get(item.id);
        
        if (!originalData) {
          // If not in original data, include it (might be a new item)
          menusWithChangedSortOrder.push({
            id: item.id,
            name: item.name,
            endpoint: item.endpoint,
            parentId: item.parentId,
            menutabid: item.menutabid,
            sortOrder: item.sortOrder,
            ispremium: item.ispremium,
            isdisplayon: item.isdisplayon,
            isclubspecific: item.isclubspecific,
            isnewfeature: item.isnewfeature,
            icon: item.icon,
          });
          return;
        }
        
        const originalSortOrder = originalData.sortOrder ?? null;
        const newSortOrder = item.sortOrder;
        
        // Only include if sortOrder changed
        if (newSortOrder !== originalSortOrder) {
          menusWithChangedSortOrder.push({
            id: item.id,
            name: item.name,
            endpoint: item.endpoint,
            parentId: item.parentId,
            menutabid: item.menutabid,
            sortOrder: item.sortOrder,
            ispremium: item.ispremium,
            isdisplayon: item.isdisplayon,
            isclubspecific: item.isclubspecific,
            isnewfeature: item.isnewfeature,
            icon: item.icon,
          });
        }
      });

      // ============================================
      // 3. VALIDATE SORT ORDERS - Check for conflicts
      // ============================================
      if (menusWithChangedSortOrder.length > 0) {
        const conflicts = validateSortOrders(menusWithChangedSortOrder);
        
        if (conflicts.length > 0) {
          console.error("Sort order conflicts detected:", conflicts);
          const conflictMessages = conflicts.map(conflict => 
            `"${conflict.menu1.name}" and "${conflict.menu2.name}" both have sortOrder ${conflict.sortOrder}`
          ).join("\n");
          
          Common.showToast(
            `Sort order conflicts detected:\n${conflictMessages}`,
            "Error",
            "error"
          );
          setIsSaving(false);
          return;
        }
      }

      // ============================================
      // 4. BUILD API CALLS
      // ============================================
      const menuTabPromises = menuTabsToSave.map((menuTab) => {
        return Common.ApiService.getInstance().request(
          "AddEditMenuTab",
          {
            id: menuTab.id,
            name: menuTab.name,
            isactive: menuTab.isactive,
            sortOrder: menuTab.sortOrder,
          },
          "POST"
        );
      });

      // Send all menus with changed sortOrder in a single API call
      let menuResponse = null;
      if (menusWithChangedSortOrder.length > 0) {
        menuResponse = Common.ApiService.getInstance().request(
          "ReorderRoutes",
          {
            menus: menusWithChangedSortOrder
          },
          "POST"
        );
      }

      // ============================================
      // 5. EXECUTE API CALLS
      // ============================================
      const promises = [...menuTabPromises];
      if (menuResponse) {
        promises.push(menuResponse);
      }
      
      if (promises.length === 0) {
        Common.showToast("No changes to save", "Info", "info");
        setIsSaving(false);
        return;
      }

      const results = await Promise.all(promises);
      
      // ============================================
      // 6. CHECK RESULTS
      // ============================================
      const allSuccess = results.every(
        (res) => res?.status === ResultStatus.Success || res?.success
      );

      if (allSuccess) {
        const totalItemsUpdated = menuTabsToSave.length + menusWithChangedSortOrder.length;
        Common.showToast(
          `Successfully updated ${totalItemsUpdated} item${totalItemsUpdated > 1 ? 's' : ''}`,
          "Success",
          "success"
        );
        if (onReorder) {
          onReorder();
        }
        setOpen(false);
      } else {
        console.error("Some items failed to update:", results.filter(
          (res) => !(res?.status === ResultStatus.Success || res?.success)
        ));
        Common.showToast("Some items failed to update", "Error", "error");
      }
    } catch (error) {
      console.error("Error updating routes order:", error);
      Common.showToast("Failed to update routes order", "Error", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const renderTreeItem = (item, level = 0, parentId = null) => {
    const isDragged = draggedItem?.id === item.id;
    const isDragOver = dragOverItem?.item?.id === item.id;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.id || item.key);
    const itemId = item.id || item.key;

    return (
      <>
        <TableRow
          key={itemId}
          draggable={true}
          onDragStart={(e) => handleDragStart(e, item)}
          onDragOver={(e) => handleDragOver(e, item, parentId)}
          onDrop={(e) => handleDrop(e, item, parentId)}
          onDragEnd={handleDragEnd}
          className={`transition-colors ${
            item.isMenutab 
              ? "bg-primary/5 font-semibold cursor-move" 
              : "cursor-move"
          } ${
            isDragged
              ? "bg-primary/20 border-2 border-primary"
              : isDragOver
              ? "bg-primary/10 border-2 border-dashed border-primary"
              : "hover:bg-muted/50"
          }`}
        >
          <TableCell className="w-12">
            <Icon
              icon="mdi:drag-vertical"
              className="h-5 w-5 text-muted-foreground"
            />
          </TableCell>
          <TableCell className="font-medium">
            <div className="flex items-center gap-2" style={{ paddingLeft: `${level * 20}px` }}>
              {hasChildren && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(itemId);
                  }}
                  className="p-1 hover:bg-muted rounded transition-colors"
                >
                  <Icon
                    icon={isExpanded ? "mdi:chevron-down" : "mdi:chevron-right"}
                    className="h-4 w-4 text-muted-foreground"
                  />
                </button>
              )}
              {!hasChildren && level > 0 && (
                <Icon icon="mdi:subdirectory-arrow-right" className="h-4 w-4 text-muted-foreground ml-6" />
              )}
              <span className={item.isMenutab ? "text-primary font-semibold" : ""}>
                {item.name || item.title}
              </span>
              {hasChildren && (
                <span className="text-xs text-muted-foreground ml-2">
                  ({item.children.length})
                </span>
              )}
            </div>
          </TableCell>
          <TableCell className="text-sm text-muted-foreground">
            {item.endpoint || "-"}
          </TableCell>
          <TableCell className="text-sm text-muted-foreground">
            {item.isMenutab ? item.title : (item.menutab || "Others")}
          </TableCell>
          <TableCell className="text-sm">
            {editingSortOrder === itemId ? (
              <Input
                type="number"
                min="1"
                value={sortOrderValue}
                onChange={(e) => setSortOrderValue(e.target.value)}
                onBlur={() => {
                  const numValue = parseInt(sortOrderValue);
                  if (!isNaN(numValue) && numValue >= 1) {
                    handleSortOrderChange(item, numValue);
                  } else {
                    cancelEditingSortOrder();
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const numValue = parseInt(sortOrderValue);
                    if (!isNaN(numValue) && numValue >= 1) {
                      handleSortOrderChange(item, numValue);
                    } else {
                      cancelEditingSortOrder();
                    }
                  } else if (e.key === "Escape") {
                    cancelEditingSortOrder();
                  }
                }}
                className="w-20 h-8 text-sm"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  startEditingSortOrder(item);
                }}
                className="cursor-pointer hover:bg-muted/50 px-2 py-1 rounded transition-colors min-w-[3rem] inline-block"
                title="Click to edit"
              >
                {getCurrentSortOrder(item)}
              </div>
            )}
          </TableCell>
        </TableRow>
        {hasChildren && isExpanded && (
          <>
            {item.children.map((child) => renderTreeItem(child, level + 1, item.id || item.key))}
          </>
        )}
      </>
    );
  };

  const handleOpenChange = (isOpen) => {
    if (!isOpen && !isSaving && !isMenuTabSubmitting) {
      setOpen(false);
      setActiveTab("reorder");
      setMenuTabFormOpen(false);
      setMenuTabEditId(null);
      setMenuTabSelectedItem(null);
    }
  };

  // Menu Tab handlers
  const handleMenuTabDelete = async (id) => {
    try {
      const isConfirmed = await Common.AntDesignModal.showDanger({
        title: "Delete Menu Tab",
        text: "Are you sure you want to delete this menu tab? This action cannot be undone.",
        confirmButtonText: "Yes, Delete",
        cancelButtonText: "Cancel"
      });

      if (isConfirmed) {
        try {
          let resp = await Common.ApiService.getInstance().request(
            `DeleteMenuTab?id=${id}`,
            {},
            "Delete"
          );

          if (resp?.status === "Success" || resp?.success) {
            Common.AntDesignMessage.success(resp.message || "Menu tab deleted successfully");
            setTimeout(() => {
              fetchMenuTabs();
              if (onReorder) {
                onReorder();
              }
            }, 500);
          } else {
            Common.AntDesignMessage.error(resp.message || "Failed to delete menu tab");
          }
        } catch (error) {
          console.error("Error deleting menu tab:", error);
          Common.AntDesignMessage.error("Failed to delete menu tab");
        }
      }
    } catch (error) {
      // User cancelled the confirmation
    }
  };

  const handleMenuTabEdit = (item) => {
    setMenuTabEditId(item.id);
    setMenuTabSelectedItem(item);
    setMenuTabFormOpen(true);
  };

  const handleMenuTabAdd = () => {
    setMenuTabEditId(null);
    setMenuTabSelectedItem(null);
    setMenuTabFormOpen(true);
  };

  const handleMenuTabClose = () => {
    setMenuTabFormOpen(false);
    setMenuTabEditId(null);
    setMenuTabSelectedItem(null);
    fetchMenuTabs();
    if (onReorder) {
      onReorder();
    }
  };

  const handleMenuTabSubmit = async (values) => {
    if (isMenuTabSubmitting) {
      return;
    }
    
    setIsMenuTabSubmitting(true);
    
    try {
      const payload = {
        id: menuTabEditId || null,
        name: values.name,
        isactive: values.isactive || false,
      };
      
      const response = await Common.ApiService.getInstance().request(
        "AddEditMenuTab",
        payload,
        "POST"
      );

      if (response?.status === ResultStatus.Success || response?.success) {
        Common.showToast(response?.message || "Saved successfully", "Success", "success");
        handleMenuTabClose();
      } else {
        Common.showToast(response?.message || "Failed to save", "Error", "error");
      }
    } catch (error) {
      Common.showToast("Failed to save", "Error", "error");
      console.error(error);
    } finally {
      setIsMenuTabSubmitting(false);
    }
  };

  // Menu tab validation schema
  const menuTabValidationSchema = yup.object({
    name: yup.string().required("Name is required"),
    isactive: yup.boolean(),
  });

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-[95vw] lg:max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon icon="mdi:menu" className="h-5 w-5" />
            Manage Routes & Menu Tabs
          </DialogTitle>
          <DialogDescription>
            Reorder routes and manage menu tabs in one place
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 flex-1 flex flex-col min-h-0">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full flex flex-col flex-1 min-h-0">
            <TabsList className="grid w-full grid-cols-2 bg-gray-100 p-1 rounded-lg mb-4 flex-shrink-0">
              <TabsTrigger
                value="reorder"
                className="data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm transition-all duration-200"
              >
                <Icon icon="mdi:drag" className="w-4 h-4 mr-2" />
                Reorder Routes
              </TabsTrigger>
              <TabsTrigger
                value="menutabs"
                className="data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm transition-all duration-200"
              >
                <Icon icon="mdi:menu" className="w-4 h-4 mr-2" />
                Manage Menu Tabs
              </TabsTrigger>
            </TabsList>

            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              {/* Reorder Routes Tab */}
              <TabsContent value="reorder" className="flex-1 flex flex-col min-h-0 mt-0 data-[state=inactive]:hidden">
                <p className="text-sm text-muted-foreground mb-4 flex-shrink-0">
                  Drag and drop routes, menus, and sub-menus to reorder them. Click the chevron to expand/collapse parent items. The order will be saved when you click Save.
                </p>

                <div className="border rounded-lg overflow-auto flex-1 min-h-0">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background z-10">
                      <TableRow>
                        <TableHead className="w-12"></TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Endpoint</TableHead>
                        <TableHead>Menu Tab</TableHead>
                        <TableHead>Sort Order</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {treeData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                            No routes available
                          </TableCell>
                        </TableRow>
                      ) : (
                        treeData.map((item) => renderTreeItem(item, 0, null))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              {/* Manage Menu Tabs Tab */}
              <TabsContent value="menutabs" className="flex-1 flex flex-col min-h-0 mt-0 data-[state=inactive]:hidden">
                <div className="flex justify-between items-center mb-4 flex-shrink-0">
                  <p className="text-sm text-muted-foreground">
                    Manage menu tabs. Add, edit, or delete menu tabs to organize your application navigation.
                  </p>
                  <Button onClick={handleMenuTabAdd}>
                    <Icon icon="mdi:plus" className="mr-2 h-4 w-4" />
                    Add Menu Tab
                  </Button>
                </div>

                <div className="border rounded-lg overflow-auto flex-1 min-h-0">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background z-10">
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created On</TableHead>
                        <TableHead className="w-24">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {menuTabsData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                            No menu tabs available
                          </TableCell>
                        </TableRow>
                      ) : (
                        menuTabsData.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.name}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded text-xs ${
                                item.isactiveValue ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                              }`}>
                                {item.isactive}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">{item.createdOn}</TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleMenuTabEdit(item)}
                                  className="h-8 w-8 p-0"
                                >
                                  <Icon icon="mdi:pencil" className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleMenuTabDelete(item.id)}
                                  className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                >
                                  <Icon icon="mdi:delete" className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>

        <DialogFooter className="mt-6">
          {activeTab === "reorder" && (
            <>
              <DialogClose asChild>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              </DialogClose>
              <Button
                onClick={handleSave}
                disabled={isSaving || treeData.length === 0}
              >
                {isSaving ? (
                  <>
                    <Icon icon="mdi:loading" className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Icon icon="mdi:content-save" className="mr-2 h-4 w-4" />
                    Save Order
                  </>
                )}
              </Button>
            </>
          )}
          {activeTab === "menutabs" && (
            <DialogClose asChild>
              <Button
                type="button"
                variant="outline"
              >
                Close
              </Button>
            </DialogClose>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Add/Edit Menu Tab Form Dialog */}
    <Dialog open={menuTabFormOpen} onOpenChange={setMenuTabFormOpen}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{menuTabEditId ? "Edit" : "Add"} Menu Tab</DialogTitle>
          <DialogDescription>
            {menuTabEditId ? "Update the menu tab details" : "Create a new menu tab for organizing your application navigation"}
          </DialogDescription>
        </DialogHeader>

        <Formik
          initialValues={{
            name: menuTabSelectedItem?.name ?? "",
            isactive: menuTabSelectedItem?.isactiveValue ?? true,
          }}
          enableReinitialize
          validationSchema={menuTabValidationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleMenuTabSubmit}
        >
          {(formikProps) => (
            <form onSubmit={formikProps.handleSubmit}>
              <div className="space-y-4 mt-4">
                <FormikInput
                  name="name"
                  label="Menu Tab Name"
                  placeholder="Enter menu tab name"
                  formikProps={formikProps}
                />
                <div className="flex items-center mt-4">
                  <AppCheckBox
                    text="Active"
                    checked={Boolean(formikProps.values.isactive)}
                    onChange={(checked) =>
                      formikProps.setFieldValue("isactive", checked)
                    }
                    className="border-default-300"
                  />
                </div>
              </div>

              <DialogFooter className="mt-6">
                <DialogClose asChild>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isMenuTabSubmitting}
                    onClick={() => setMenuTabFormOpen(false)}
                  >
                    Cancel
                  </Button>
                </DialogClose>
                <Button
                  type="submit"
                  disabled={isMenuTabSubmitting || formikProps.isSubmitting}
                >
                  {isMenuTabSubmitting ? "Saving..." : menuTabEditId ? "Update" : "Save"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ReorderRoutes;

